'use client';

import Cookie from 'js-cookie';
import { type PropsWithChildren, useCallback, useMemo } from 'react';
import { AddBillingAddressRequestBody, AddDonationRequestBody, AddItemRequestBody, AddPaymentMethodRequestBody, AddPrescriptionBody, AddShippingAddressRequestBody, CartUI, DeliveryOptionsPost, GuestEmailRequestBody, PickupInfoRequestBody, PlaceOrderRequestBody, SetShippingMethodRequestBody, TreatPointRequestBody, UpdateItemFulfillmentMethodRequestBody, UpdateItemQtyRequestBody, UpdateItemStoreRequestBody, UpdateSddDropoffAddressRequestBody, UpdateShippingAddressRequestBody } from '@libs/common-node';
import { AUTH_COOKIES, COOKIES, DeliveryType } from '@libs/core';
import { LayoutContent } from '../components/LayoutContent';
import { CART_ERROR_MESSAGES, COMPONENTS } from '../constants';
import { useCartNotifications } from '../hooks';
import type { EstimatedLocation } from '../hooks/useEstimatedLocation';
import { useEstimatedLocation } from '../hooks/useEstimatedLocation';
import { usePageLayout } from '../hooks/usePageLayout';
import type { UpdateItemFulfillmentMethodOptions } from '../types';
import { addBillingAddress, addDiscountCode, addDonation, addGuestEmail, addItem as addItemToCart, addLegacyPrescription, addPayment, addPickupInfo, addPrescription, addShippingAddress, addTreatPoints as addTreatPointsToCart, getDeliveryOptions, getShippingMethods, logConsoleError, placeOrder, removeDiscountCode, removeDonation, removeItem, removePayment as removePaymentFromCart, removeTreatPoints, selectExistingShippingAddress, setDeliveryOptions, setShippingMethods, updateItemFulfillmentMethod, updateItemQty, updateItemStore, updateSddDropoffAddress, updateShippingAddress, updateSubscriptionStatus, validateShippingAddresses } from '../utils';
import { applyFormatPriceFraction } from '../utils/applyFormatPriceFraction';
import { isAddItemCartResponseSuccessful, summarizeAddItemCartResponseForLog } from '../utils/isAddItemCartResponseSuccessful';
import { CartContext } from './CartContext';
import { canProductProceed } from './utils/canProductProceed';
import { resolveCartForAddItem, shouldRetryAddItemAsNewCart } from './utils/cartIdentity';
import useCartState from './utils/useCartState';
import useTreatsInCart from './utils/useTreatsInCart';
export { CartContext } from './CartContext';
const enrichBodyWithEstimatedLocation = (body: AddItemRequestBody, getEstimatedLocation: () => EstimatedLocation | null) => {
  if (body.latitude == null && (body.fulfillmentMethod === DeliveryType.STH || body.fulfillmentMethod === DeliveryType.AUTOSHIP)) {
    const loc = getEstimatedLocation();
    if (loc) {
      return {
        ...body,
        latitude: loc.latitude,
        longitude: loc.longitude
      };
    }
  }
  return body;
};
export function CartProvider({
  children
}: PropsWithChildren) {
  const {
    user
  } = usePageLayout();
  const cartId = Cookie.get(COOKIES.CART_ID);
  const {
    cart,
    updateCartState,
    cartShippingOptionsMessage,
    setCartShippingOptionsMessage,
    errors,
    handleCartError,
    ...restOfCartState
  } = useCartState(cartId as string);
  const {
    setNotifications
  } = useCartNotifications();
  const {
    getEstimatedLocation
  } = useEstimatedLocation();
  const {
    estimatedTreatsPoints,
    getEstimatedTreatsPoints,
    isGetEstimatedTreatsPointsLoading
  } = useTreatsInCart(cart, user);

  /**
   * Asynchronously adds an item to the cart using provided details, subject to business rules.
   *
   * The function first checks if the item can be added and if the cart state is ready (either has a valid ID and version
   * or no cart ID is set). If conditions are met, it proceeds to add the item using `addItemToCart` and updates the cart state.
   * If a new cart is created, the cart ID is stored in cookies. It also handles cart updating state by toggling `setUpdating`.
   *
   * @param {AddItemRequestBody} body - Request body for /carts/items BFF endpoint. Please refer to AddItemRequestBody type for more details.
   * @returns {Promise<Cart | null>} A promise resolving with the updated cart object or null if unable to add.
   */
  const addItem = useCallback(async (body: AddItemRequestBody, newCart?: CartUI | null) => {
    let cartObj = newCart || cart;
    const {
      setUpdating,
      setCart,
      enrichCart,
      refetchCart,
      dropHeldCart
    } = restOfCartState;
    setUpdating(true);
    try {
      let cartIDCookie = Cookie.get(COOKIES.CART_ID);

      // The cookie is shared by every tab, so it can already name a different cart, or none,
      // once another tab has checked out, signed out, or merged carts. Reconcile first rather
      // than mutating a cart this tab may no longer own.
      const {
        cart: reconciledCart,
        didDropHeldCart
      } = await resolveCartForAddItem({
        heldCart: cartObj,
        cartIdCookie: cartIDCookie,
        refetchCart
      });

      // copy rather than alias; the ternary keeps a cleared cookie null instead of `{}`.
      cartObj = reconciledCart ? {
        ...reconciledCart
      } : null;
      if (didDropHeldCart) {
        dropHeldCart();
      }
      if (!cartObj?.id) {
        cartIDCookie = undefined;
      }

      // no cart cookie or cart id means this tab is holding no cart.
      let isNewCart = !cartIDCookie && !cartObj?.id;
      if (!canProductProceed(cartIDCookie, cartObj)) {
        return null;
      }
      const enrichedBody = enrichBodyWithEstimatedLocation(body, getEstimatedLocation);
      let ctCart = await addItemToCart({
        cartId: cartObj?.id,
        version: cartObj?.version,
        body: enrichedBody
      });

      // a rejected add against an existing cart can mean that cart is already gone, for
      // example ordered in another tab before its cookie was cleared. Drop it before the
      // retry so a failing retry cannot leave this tab holding a cart known to be gone.
      if (await shouldRetryAddItemAsNewCart(ctCart, cartObj)) {
        dropHeldCart();
        cartObj = null;
        isNewCart = true;
        ctCart = await addItemToCart({
          body: enrichedBody
        });
      }
      if (ctCart?.id && !isAddItemCartResponseSuccessful(ctCart)) {
        logConsoleError(CART_ERROR_MESSAGES.ADD_ITEM_RESPONSE_INVALID_SHAPE, {
          component: COMPONENTS.CART_PROVIDER,
          severity: 'HIGH',
          additionalInfo: summarizeAddItemCartResponseForLog(ctCart)
        });
        return null;
      }
      if (ctCart?.id) {
        // Ensure new/updated items get enriched fields (productUrl, autoshipDiscount, etc.)
        // before writing to state; otherwise preserve logic can lock in missing enrichment.
        const enrichedCart = await enrichCart(ctCart, cartObj ?? null);
        setCart(enrichedCart);
        if (enrichedCart?.notifications) {
          setNotifications(enrichedCart?.notifications ?? []);
        }
        if (isNewCart) {
          Cookie.set(COOKIES.CART_ID, ctCart.id);
        }
        return enrichedCart;
      } else {
        logConsoleError(CART_ERROR_MESSAGES.ADD_ITEM_FAILED, {
          component: COMPONENTS.CART_PROVIDER,
          severity: 'HIGH',
          additionalInfo: `Cart: ${JSON.stringify(ctCart)}`,
          extra: {
            cart: JSON.stringify(ctCart)
          }
        });
      }
      return ctCart;
    } catch (e) {
      logConsoleError(JSON.stringify(e), {
        component: COMPONENTS.CART_PROVIDER,
        severity: 'HIGH',
        additionalInfo: 'Exception occurred while adding item to cart'
      });
      return null;
    } finally {
      setUpdating(false);
    }
  }, [cart, restOfCartState, getEstimatedLocation]);

  /**
   * The Cart API currently doesn't allow adding multiple products at once.
   * To enable this, we send several requests one after another.
   *
   * This approach is temporary and will be updated once the cart supports bulk operations.
   */
  const addItems = useCallback(async (body_0: AddItemRequestBody[], newCart_0?: CartUI | null) => {
    let latestCartObject = newCart_0 || cart;
    if (body_0.length === 1) {
      return await addItem(body_0[0], newCart_0);
    }
    const {
      setUpdating: setUpdating_0
    } = restOfCartState;
    for (const addItemBody of body_0) {
      try {
        latestCartObject = await addItem(addItemBody, latestCartObject);
        setUpdating_0(true); // addItem will setUpdating to false once done

        // Since addItem returns null on failure, we have to explicitly throw an error ourselves
        if (!latestCartObject) {
          throw new Error(`Exception occured while adding product with sku ${addItemBody.sku} to cart`);
        }
      } catch (error) {
        logConsoleError(JSON.stringify(error), {
          component: COMPONENTS.CART_PROVIDER,
          severity: 'HIGH',
          additionalInfo: 'Exception occured while adding multiple items to cart'
        });
        return null;
      } finally {
        setUpdating_0(false);
      }
    }
    return latestCartObject;
  }, [cart, addItem, restOfCartState]);
  const removeItemFromCart = useCallback(async (itemId: string) => updateCartState(removeItem, null, itemId), [updateCartState]);
  const removePayment = useCallback(async (methodId: string) => updateCartState(removePaymentFromCart, {
    methodId
  }), [updateCartState]);
  const addTreatPoints = useCallback(async (treatPointsDetails: TreatPointRequestBody) => updateCartState(addTreatPointsToCart, {
    ...treatPointsDetails,
    loyaltyId: user?.loyaltyid || null
  }), [updateCartState, user?.loyaltyid]);
  const getCartShippingMethods = useCallback(async () => {
    if (!cart) {
      return null;
    }
    const response = await getShippingMethods({
      cartId: cart?.id
    });
    if (response) {
      restOfCartState.setShippingMethodsList(response);
    }
    return response;
  }, [cart]);
  const getCartDeliveryOptions = useCallback(async () => {
    if (!cart) {
      return null;
    }
    return await getDeliveryOptions({
      cartId: cart?.id
    });
  }, [cart]);
  const contextValue = useMemo(() => ({
    cart: cart ? applyFormatPriceFraction(cart) : null,
    addItem,
    addItems,
    estimatedTreatsPoints,
    updateItemQuantity: (itemId_0: string, quantity: number) => {
      restOfCartState.clearStockErrors();
      return updateCartState<UpdateItemQtyRequestBody>(updateItemQty, {
        quantity
      }, itemId_0 as string);
    },
    cartShippingOptionsMessage,
    changeCartShippingOptionMessage: (message: string) => {
      setCartShippingOptionsMessage(message);
    },
    updateItemFulfillmentMethod: async (props: UpdateItemFulfillmentMethodRequestBody, options?: UpdateItemFulfillmentMethodOptions) => {
      const {
        clearStockErrors
      } = restOfCartState;
      clearStockErrors();
      let enrichedProps = props;
      if (props.fulfillmentMethod === DeliveryType.STH || props.fulfillmentMethod === DeliveryType.AUTOSHIP) {
        const loc = getEstimatedLocation();
        if (loc && !('latitude' in props && props.latitude != null)) {
          enrichedProps = {
            ...props,
            latitude: loc.latitude,
            longitude: loc.longitude
          };
        }
      }
      const result = await updateCartState<UpdateItemFulfillmentMethodRequestBody>(updateItemFulfillmentMethod, enrichedProps, enrichedProps.itemId, {
        validateCartOnError: true,
        ...options
      });
      return result;
    },
    addCartBillingAddress: (address: AddBillingAddressRequestBody) => updateCartState<AddBillingAddressRequestBody>(addBillingAddress, {
      ...address
    }),
    addCartShippingAddress: async (address_0: AddShippingAddressRequestBody & {
      deliveryInstructionsPostMvp?: boolean;
    }) => {
      const result_0 = await updateCartState(addShippingAddress, {
        ...address_0
      });
      return result_0;
    },
    updateCartShippingAddress: (address_1: UpdateShippingAddressRequestBody & {
      deliveryInstructionsPostMvp?: boolean;
    }) => updateCartState(updateShippingAddress, {
      ...address_1
    }),
    selectExistingCartShippingAddress: (addressId: number) => updateCartState<{
      addressId: number;
    }>(selectExistingShippingAddress, {
      addressId
    }),
    updateSddDropoffAddress: (body_1: UpdateSddDropoffAddressRequestBody) => updateCartState<UpdateSddDropoffAddressRequestBody>(updateSddDropoffAddress, {
      ...body_1
    }),
    addPayment: (paymentDetails: AddPaymentMethodRequestBody) => updateCartState<AddPaymentMethodRequestBody>(addPayment, {
      ...paymentDetails
    }),
    removePayment,
    removeItemFromCart,
    updateStoreOnCartItem: (itemId_1: string, storeId: string) => updateCartState(updateItemStore, {
      storeId
    } as UpdateItemStoreRequestBody, itemId_1),
    addCartDonation: (amount: number, fractionDigits: number, isRoundUpEnabled?: boolean) => updateCartState<AddDonationRequestBody>(addDonation, {
      amount,
      fractionDigits,
      isRoundUpEnabled
    }),
    setCartShippingMethods: (shippingOption: SetShippingMethodRequestBody) => updateCartState<SetShippingMethodRequestBody>(setShippingMethods, shippingOption),
    setCartDeliveryOptions: (deliveryOption: DeliveryOptionsPost) => updateCartState<DeliveryOptionsPost>(setDeliveryOptions, deliveryOption),
    removeCartDonation: () => updateCartState(removeDonation),
    addEmail: (emailAddress: string, emailOptIn = false) => updateCartState<GuestEmailRequestBody>(addGuestEmail, {
      emailAddress,
      emailOptIn
    } as unknown as GuestEmailRequestBody),
    addDiscountCode: (code: string) => updateCartState(addDiscountCode, {
      code
    }),
    removeDiscountCode: async (codeId: string) => updateCartState(removeDiscountCode, {
      codeId
    }),
    addPickupInfo: async (pickupInfo: PickupInfoRequestBody) => updateCartState(addPickupInfo, pickupInfo),
    placeOrder: async (placeOrderBody: PlaceOrderRequestBody) => {
      restOfCartState.setUpdating(true);
      if (!cart) {
        return null;
      }
      return await placeOrder({
        cartId: cart.id,
        body: placeOrderBody,
        version: cart.version
      });
    },
    addTreatPoints,
    removeTreatPoints: () => updateCartState(removeTreatPoints),
    getEstimatedTreatsPoints,
    getCartShippingMethods,
    getCartDeliveryOptions,
    ...restOfCartState,
    validateShippingAddresses: async () => {
      const isAuthenticated = Cookie.get(AUTH_COOKIES.ACCESS_TOKEN);
      if (!isAuthenticated) {
        return null;
      }
      return validateShippingAddresses(cart?.id as string);
    },
    addLegacyPrescription: async (itemId_2: string, body_2: AddPrescriptionBody) => updateCartState(addLegacyPrescription, body_2, itemId_2),
    addPrescription: async (itemId_3: string, body_3: AddPrescriptionBody) => updateCartState(addPrescription, body_3, itemId_3),
    updateSubscriptionStatus: async (cartId_0: string, version?: number) => updateSubscriptionStatus({
      cartId: cartId_0,
      version: (version ?? cart?.version) as number
    }),
    errors,
    handleCartError,
    setCartShippingOptionsMessage,
    isGetEstimatedTreatsPointsLoading
  }), [cart, addItem, addItems, estimatedTreatsPoints, removePayment, removeItemFromCart, addTreatPoints, getEstimatedTreatsPoints, getCartShippingMethods, getCartDeliveryOptions, restOfCartState, updateCartState, errors, handleCartError, cartShippingOptionsMessage, setCartShippingOptionsMessage, isGetEstimatedTreatsPointsLoading]);
  return <LayoutContent>
            <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
        </LayoutContent>;
}
export { useCartState };